/**
 * @description Color utility functions for the employee portal branding system
 *
 * Key features:
 * - Hex to HSL conversion for CSS custom properties
 * - Auto-generate brand color variants from primary/secondary
 * - Inject CSS custom properties onto document root
 *
 * @notes
 * - Mirrors frontend/src/utils/colorUtils.js (kept separate to avoid cross-workspace deps)
 * - CSS custom properties use HSL channel format (e.g., "239 84% 67%") without hsl() wrapper
 *   to enable Tailwind opacity modifiers like bg-primary/10
 */

/**
 * Convert hex color to HSL values
 * @param {string} hex - Hex color (e.g., '#3b82f6' or '#abc')
 * @returns {{ h: number, s: number, l: number }}
 */
export function hexToHSL(hex) {
  let r = 0, g = 0, b = 0;

  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex.substring(1, 3), 16);
    g = parseInt(hex.substring(3, 5), 16);
    b = parseInt(hex.substring(5, 7), 16);
  }

  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
      default:
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
}

/**
 * Format HSL values as CSS channel string
 * @param {{ h: number, s: number, l: number }} hsl
 * @returns {string} e.g., "239 84% 67%"
 */
function hslToChannels(hsl) {
  return `${hsl.h} ${hsl.s}% ${hsl.l}%`;
}

/**
 * Generate brand color variants from primary and secondary hex colors
 * @param {string} primaryHex
 * @param {string} secondaryHex
 * @returns {object} CSS custom property name → HSL channel string
 */
export function generateBrandVariants(primaryHex, secondaryHex) {
  const primary = hexToHSL(primaryHex);
  const secondary = hexToHSL(secondaryHex);

  return {
    '--brand-primary': hslToChannels(primary),
    '--brand-primary-hover': hslToChannels({ ...primary, l: Math.max(0, primary.l - 10) }),
    '--brand-primary-light': hslToChannels({ ...primary, l: 93 }),
    '--brand-secondary': hslToChannels(secondary),
    '--brand-secondary-hover': hslToChannels({ ...secondary, l: Math.max(0, secondary.l - 10) }),
    '--brand-secondary-light': hslToChannels({ ...secondary, l: 93 })
  };
}

/**
 * Inject brand CSS custom properties onto document.documentElement
 * @param {object} variants - Output from generateBrandVariants
 */
export function injectBrandCSS(variants) {
  const root = document.documentElement;
  for (const [property, value] of Object.entries(variants)) {
    root.style.setProperty(property, value);
  }
}
